import { useMutationHook, useQueryHook } from "@/hooks"

import {
  AccountBaseType,
  CreateProfileImagePayload,
  CreateProfileImageResponse,
} from "@/types/account"
import { UseQueryOptions } from "@tanstack/react-query"
import { ApiError, ApiResponse } from "@/app/api/type"

import {
  verifyAuthCode,
  createInviteEmail,
  createProfileImage,
  createTableauToken,
  createTableauView,
  createTableauViews,
  createTableauWorkbooks,
  createVerifyEmail,
  deleteProfileImage,
  readAccountInfo,
  upsertPassword,
  upsertUserInfo,
  getCountryList,
  upsertLanguage,
} from "@/app/api/account"
import { readActivitySearchList } from "@/app/api/activity"

export const useAccountInfoQuery = (idfAccount: number) => {
  return useQueryHook({
    queryKey: ["readAccountInfo"],
    queryFn: async () => {
      const { response, success, error } = await readAccountInfo(idfAccount)
      if (response) {
        response.hasTableauRole = false
      }

      return { response, success, error }
    },
    options: {
      enabled: !!idfAccount,
    } as UseQueryOptions<AccountBaseType, ApiError>,
  })
}

export const useVerifyAuthCodeMutation = () => {
  return useMutationHook(verifyAuthCode)
}

export const useCreateInviteEmailMutation = () => {
  return useMutationHook(createInviteEmail)
}
export const useCreateVerifyEmailMutation = () => {
  return useMutationHook(createVerifyEmail)
}

export const useUpsertUserInfoMutation = () => {
  return useMutationHook(upsertUserInfo, ["readAccountInfo"])
}

export const useCreateProfileImageMutation = () => {
  return useMutationHook<
    ApiResponse<CreateProfileImageResponse>,
    ApiError,
    CreateProfileImagePayload
  >(createProfileImage)
}

export const useDeleteProfileImageMutation = () => {
  return useMutationHook(deleteProfileImage)
}

export const useUpsertPasswordMutation = () => {
  return useMutationHook(upsertPassword)
}

export const useCreateTableauTokenMutation = () => {
  return useMutationHook(createTableauToken)
}
export const useCreateTableauViewsMutation = () => {
  return useMutationHook(createTableauViews)
}

export const useCreateTableauWorkbooksMutation = () => {
  return useMutationHook(createTableauWorkbooks)
}
export const useCreateTableauViewMutation = () => {
  return useMutationHook(createTableauView)
}

export const useGetCountryListQuery = () => {
  return useQueryHook({
    queryKey: ["countryList"],
    queryFn: () => getCountryList(),
  })
}

export const useUpsertLanguageMutation = () => {
  return useMutationHook(upsertLanguage, ["readAccountInfo"])
}
